import { getTimestampFromLocalStorage } from "./localStorage";

import { LAST_UPDATED } from "../Queries/LastUpdated";
import { COMMERCIAL_VIOLATION_CONTAINERS } from "../Queries/CommercialViolationContainers";
import { GET_TICKET_VIOLATIONS } from "../Queries/GetTicketViolations";
import {
  VEHICLE_MAKE_QUERY,
  VEHICLE_MODEL_QUERY,
} from "../Queries/SetupListsQuery";
import { GET_OFFENSE_VIOLATIONS } from "../Queries/GetOffenseViolations";
import { getValueFromPath } from "./util.js";
import { localStoreData } from "../utils/utils";

/********* start table setup ***********/
export const QUERY_LOOKUP = {
  ticket_violations: {
    query: {
      query: GET_TICKET_VIOLATIONS,
      variables: { warnOnly: false },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
    dataPath: "getTicketViolationPrototypes",
    localStorageName: "ticket_table_version",
    lastUpdatedQuery: {
      query: LAST_UPDATED,
      variables: { modelName: "TicketViolationPrototype" },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
  },
  ticket_violations_warnings: {
    query: {
      query: GET_TICKET_VIOLATIONS,
      variables: { warnOnly: true },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
    dataPath: "getTicketViolationPrototypes",
    localStorageName: "ticket_table_warnings_version",
    lastUpdatedQuery: {
      query: LAST_UPDATED,
      variables: { modelName: "TicketViolationPrototype" },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
  },
  municipal_violations: {
    query: {
      query: GET_TICKET_VIOLATIONS,
      variables: { muniVios: true },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
    dataPath: "getTicketViolationPrototypes",
    localStorageName: "municipal_violation_table_version",
    lastUpdatedQuery: {
      query: LAST_UPDATED,
      variables: { modelName: "TicketViolationPrototype" },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
  },
  commercial_violation_containers: {
    query: {
      query: COMMERCIAL_VIOLATION_CONTAINERS,
      variables: { oriId: 1, noSessionReset: true },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
    dataPath: "setupLists.commercialViolationContainers",
    localStorageName: "commercial_violation_table_version",
    lastUpdatedQuery: {
      query: LAST_UPDATED,
      variables: { modelName: "TicketViolationPrototype" },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
  },
  vehicle_makes: {
    query: {
      query: VEHICLE_MAKE_QUERY,
      variables: { oriId: 1, noSessionReset: true },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
    dataPath: "setupLists.vehicleMakes",
    localStorageName: "vehicle_make_table_version",
    lastUpdatedQuery: {
      query: LAST_UPDATED,
      variables: { modelName: "VehicleMake" },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
  },
  vehicle_models: {
    query: {
      query: VEHICLE_MODEL_QUERY,
      variables: { oriId: 1, noSessionReset: true },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
    dataPath: "setupLists.vehicleModels",
    localStorageName: "vehicle_model_table_version",
    lastUpdatedQuery: {
      query: LAST_UPDATED,
      variables: { modelName: "VehicleModel" },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
  },
  offense_violations: {
    query: {
      query: GET_OFFENSE_VIOLATIONS,
      variables: { noSessionReset: true },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
    dataPath: "getOffenseViolationPrototypes",
    localStorageName: "offense_violation_table_version",
    lastUpdatedQuery: {
      query: LAST_UPDATED,
      variables: { modelName: "OffenseViolationPrototype" },
      fetchPolicy: "network-only",
      context: { timeout: 60000 },
    },
  },
};

//exported for testing
export const clearAllStoredData = async (tableNames, db, canClear = () => true) => {
  if (!canClear()) return false;
  const tables = tableNames.map(name => db[name]).filter(Boolean);
  if (!tables.length) return true;
  const sessionChanged = new Error("Session changed during cache cleanup");
  try {
    // A rejected guard aborts the transaction, restoring any tables already
    // cleared. Other tabs' writes to these tables wait for this transaction.
    await db.transaction("rw", tables, async () => {
      for (const table of tables) {
        if (!canClear()) throw sessionChanged;
        await table.clear();
        if (!canClear()) throw sessionChanged;
      }
    });
    return true;
  } catch (error) {
    if (error === sessionChanged) return false;
    throw error;
  }
};

/********* end exported functions for table clearing  *********/

//checks if there is a local storage data for the last time the data was fetched
//if not it returns true
//otherwise it compares that date with the result date of the last updated query for the model type
//it also counts the table to handle the case where the local storage time exists but the table is empty
//exported for testing
export const shouldUpdateTable = async (tableName, db, client) => {
  // console.log('shouldUpdateTable', tableName)
  if (!tableName) {
    return true;
  }
  const localStorageName = QUERY_LOOKUP[tableName]?.localStorageName;
  const localTimestamp = getTimestampFromLocalStorage(localStorageName);
  const table = db[tableName];
  let count = 0;

  if (!table) {
    console.error(`Table ${tableName} does not exist in the database`);
    return true;
  }

  try {
    count = await table.count();
  } catch (error) {
    if (error.name === "AbortError") {
      console.error("Transaction aborted:", error);
      // Handle the error here
    } else {
      throw error;
    }
  }
  //There's no local storage time or the table is empty so update the table
  if (!localTimestamp || count < 1) {
    return true;
  }
  //use the lookup object to find the query object for the table
  const { lastUpdatedQuery } = QUERY_LOOKUP[tableName];
  try {
    //fetch the last updated time
    const { data } = await client.query(lastUpdatedQuery);
    //parse the response to get the unix time
    const updatedTime = data[Object.keys(data)][0];
    //compare the time the data was stored to the last updated time
    return +localTimestamp < updatedTime;
  } catch (err) {
    console.error(err);
    return true;
  }
};

//If shouldUpdateTable returns true, this function will determine the
//query, the path to the data, and the local storage name for the timestamp
//it will then fetch the data, parse the data with getValueFromPath,
//then clear the table of data and bulkAdd the fetched data
//Lastly, it adds a unix timestamp for the time the data was fetched
//exported for testing
export const updateTableData = async (tableName, db, client) => {
  // console.log('updateTableData', tableName)
  try {
    if (!(await shouldUpdateTable(tableName, db, client))) {
      // console.log(tableName, 'is already up to date')
      return true;
    }

    const { query, dataPath, localStorageName } = QUERY_LOOKUP[tableName];

    let fetchedData;

    try {
      fetchedData = await client.query(query);
    } catch (err) {
      console.error(err);
      throw err;
      // return;
    }

    const table = db[tableName];
    if (!table) {
      console.error(`Table ${tableName} does not exist in the database`);
      return false;
    }

    if (!fetchedData.data) {
      console.error("The fetched data does not have the data key");
      return false;
    }
    const value = getValueFromPath(fetchedData.data, dataPath);
    if (value) {
      try {
        await table.clear();
        await table.bulkAdd(value, "id");
        // Once the data has been successfully stored, set the current time as the new timestamp for this table
        localStoreData(Date.now().toString(), localStorageName, false);
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    } else {
      console.error("There was a problem fetching the data for", tableName);
      return false;
    }
  } catch (err) {
    console.error(`Unhandled error in updateTableData for ${tableName}:`, err);
    return false;
  }
};

//this function starts the ball rolling. It uses the array of table names
//this is exported because it is called in IncidentPanels in case an incident is opened and any of the data is missing
export const checkTables = async (tableNames, db, client) => {
  if (!tableNames || !Array.isArray(tableNames)) {
    console.error("Invalid tableNames:", tableNames);
    return false;
  }
  if (!db) {
    console.error("Invalid db:", db);
    return false;
  }
  if (!client) {
    console.error("Invalid client:", client);
    return false;
  }
  // for (let tableName of tableNames) {
  //   console.log('tableNames:', tableNames)
  //   console.log(tableName, 'is being checked')
  //   return await updateTableData(tableName, db, client);
  // }
  const promises = tableNames.map(tableName => {
    // console.log(tableName, 'is being checked');
    return updateTableData(tableName, db, client);
  });
  return Promise.all(promises);
};
